<!-- Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com -->
<!-- See license file. -->
<script setup lang="ts">
import type { Ref } from "vue";
import type { Dentist } from "@/types";

import { ref, computed, watch, inject } from "vue";
import DentistDisplay from "@/components/actors/DentistDisplay.vue";
import FormInput from "@/components/FormInput.vue";
import RelatedDossier from "@/components/dossier/RelatedDossier.vue";
import { MDBSwitch } from "mdb-vue-ui-kit";
import { makeSearchEngine } from "@/logic/search";
import { dentistKey, importsKey } from "@/logic/provide";
import { useRoute } from "vue-router";
import Navigation from "../navigation/Navigation.vue";
import {
  closeSecondaryViewKey,
  navigationKey,
  openSecondaryViewKey,
} from "../navigation/navigation";

export type PrctFilter = {
  withCases: boolean;
};
const route = useRoute();
const allPrcts = inject(dentistKey);
const imports = inject(importsKey);

const openSecondaryView = inject(openSecondaryViewKey);
const closeSecondaryView = inject(closeSecondaryViewKey);
const currentNavKey = inject(navigationKey);

const latestImport = computed(() => {
  if (!imports) {
    return null;
  }
  const record = imports.value.find((i) => i.kind === "4D");
  if (!record) {
    return null;
  }

  return new Date(Date.parse(record.timestamp));
});

// Handle link into this page to setup the search filter
const searchInput = ref("");
const searchFilter: Ref<PrctFilter> = ref({
  withCases: false,
});

function updateSearchFromLink() {
  if (route.params.id) {
    const id = route.params.id as string;
    searchInput.value = id ? id : "";
    searchFilter.value.withCases = false;
  } else {
    searchInput.value = "";
    searchFilter.value.withCases = false;
  }
}
watch(route, updateSearchFromLink, { immediate: true, deep: true });

// Handles selected case
const _selectedDentist = ref(null) as Ref<Dentist | null>;
const minifiedShift = -303.04; // panel height difference between minified and full view
const initialPanelX = 40; // X placement of the event panel in the view
const panelX = ref(initialPanelX);

function dentistSelected(selected: Dentist, offset: number) {
  if (window.getSelection()?.toString()) {
    return;
  }

  if (_selectedDentist.value && _selectedDentist.value.id === selected.id) {
    _selectedDentist.value = null;
  } else {
    // When another element is selected above it will shrink its panel and the global shift must be addapted
    var selectedIsAbove = false;
    for (let i = 0; i < currentPrcts.value.length; i++) {
      if (currentPrcts.value[i].id === _selectedDentist.value?.id) {
        selectedIsAbove = true;
      }
      if (currentPrcts.value[i].id === selected.id) {
        break;
      }
    }
    const extraShift = selectedIsAbove ? minifiedShift : 0;
    _selectedDentist.value = selected;
    panelX.value = offset + extraShift + initialPanelX; // accouting for elements on top
  }
}

function deselect() {
  if (_selectedDentist.value) {
    _selectedDentist.value = null;
  }
}

const currentPrct = computed(() => _selectedDentist.value);

watch(
  _selectedDentist,
  (newVal) => {
    if (currentNavKey) {
      if (newVal) {
        openSecondaryView?.(currentNavKey);
      } else {
        closeSecondaryView?.(currentNavKey);
      }
    }
  },
  { immediate: true }
);

// Search
const searchSource = computed(() => allPrcts?.value.list || []);
const { searchResult } = makeSearchEngine(
  {
    includeScore: true,
    ignoreLocation: true,
    findAllMatches: true,
    minMatchCharLength: 3,
    keys: [
      { name: "id" },
      { name: "first_name", weight: 2 },
      { name: "last_name", weight: 3 },
      {
        name: "full_name",
        getFn: (dentist: Dentist) =>
          dentist.last_name + " " + dentist.first_name,
        weight: 6,
      },
      {
        name: "full_name_inverted",
        getFn: (dentist: Dentist) =>
          dentist.last_name + " " + dentist.first_name,
        weight: 5,
      },
      "email",
      "notes",
      { name: "cdo_id", weight: 3 },
    ],
  },
  searchInput,
  searchSource
);

watch(
  searchResult,
  () => {
    // Auto open singled-out searches
    if (searchResult.value.length === 1) {
      _selectedDentist.value = searchResult.value[0] as Dentist;
    } else {
      _selectedDentist.value = null;
    }
  },
  { immediate: true }
);

// Hanlde filtering
const filteredPrcts = computed(() => {
  const source =
    searchInput.value.length > 1
      ? (searchResult.value as Array<Dentist>)
      : allPrcts?.value.list;
  if (!source) {
    return [];
  }

  return source.filter(
    (prct: Dentist) => !searchFilter.value.withCases || prct.cases.length
  );
});

const currentPrcts = computed(() => {
  return filteredPrcts.value.slice(0, 12);
});

watch(searchFilter, deselect, { deep: true });
watch(filteredPrcts, () => {
  if (currentPrct.value) {
    if (
      filteredPrcts.value.find((a) => a.id === currentPrct.value?.id) ===
      undefined
    ) {
      deselect();
    }
  }
});
</script>

<template>
  <Teleport to="#toolbar">
    <span>Recherche dans :</span>
    <div>
      <MDBSwitch v-model="searchFilter.withCases" label="Acteur d'un dossier" />
    </div>
  </Teleport>
  <Teleport to="#searchbar">
    <FormInput v-model="searchInput" label="Rechercher" type="text" />
  </Teleport>
  <div class="practicians">
    <div class="container-prct">
      <div>
        <div class="container-stats">
          <div class="info">
            <span v-if="latestImport">
              Le dernier import 4D date du
              {{ latestImport.toLocaleDateString() }}</span
            >
          </div>
          <div class="container-count">
            <span>{{ filteredPrcts?.length || 0 }}</span>
            <span v-if="(filteredPrcts?.length || 0) <= 1"> praticien </span>
            <span v-else> praticiens </span>
          </div>
        </div>
      </div>
      <div class="container-prct-listing">
        <div>
          <br />
          <h3>Fichier des praticiens</h3>
          <span>
            Ce fichier contient les praticiens importés depuis la base 4D.</span
          ><br />
          <span
            >Vous pouvez ajouter des informations aux praticiens mais leur
            création se fait via l'import.
          </span>
        </div>
        <template v-for="prct of currentPrcts" :key="prct.id">
          <DentistDisplay
            :class="{
              hide: currentPrct && currentPrct.id !== prct.id,
              selected: currentPrct && currentPrct.id === prct.id,
            }"
            :modelValue="prct"
            :minified="!currentPrct || currentPrct.id !== prct.id"
            openMode="Edit"
            @click:detail="dentistSelected"
          />
        </template>
      </div>
    </div>
    <Navigation nav-key="Dossiers associés au praticien">
      <RelatedDossier
        :entity="currentPrct"
        :panelX="panelX"
        v-if="currentPrct != null"
      />
    </Navigation>
  </div>
</template>

<style scoped lang="scss">
.info {
  font-size: small;
}

.practicians {
  display: flex;
  flex-direction: row;
  column-gap: 3em;

  .container-prct {
    display: flex;
    flex-direction: column;
    padding-top: 1em;
    padding-left: 1em;

    .container-stats {
      display: flex;
      flex-direction: row;
      align-items: baseline;
      justify-content: space-between;
    }

    .container-count {
      text-align: right;
    }

    .container-prct-listing {
      display: flex;
      flex-direction: column;
      padding-top: 1em;
      margin-right: 1em; // to compensate for the animation of the cards
      row-gap: 4em;
      margin-bottom: 100px;
    }
  }

  .hide {
    opacity: 0.2;
  }
}
</style>
